import * as angular from 'angular';
import * as _ from 'lodash';

import {
    ArrowData,
    ComponentDefinition,
    ConnectionPoint,
    Dictionary,
    DigitalSet,
    FlatMode,
    Group,
    IDisplayedColumn,
    IIdentifierVm,
    ISortConfiguration,
    IValueViewModel,
    Identifier,
    IdentifierType,
    Qualifier,
    VariableSubType,
    VariableType,
    ObjectType,
} from '../../models';
import {
    IVariableCascadingService,
    IIdentifierStateService,
    IStoreService,
    ITranslatedLoggerService,
} from '../../services';
import { Store, StoreElement } from '../../stateManagement';
import {
    defaultItemPerPage,
    groupSaved,
    groupSelected,
    identifierDeleted,
    identifierSaved,
    identifierSelected,
    tagSaved,
    toggleEditMode
} from '../../common';
import { IRootScope } from 'IRootScope';
import { getDefaultQualifier, getDefaultSubtype } from '../../services/helpers/variable.helper';
import { isCorrectIdentifierType, isCorrectObjectType, isItself, isTagOrValidated } from './dropConditions';

class IdentifiersController implements angular.IComponentController {
    allIdentifiers: Identifier[];
    arrowData: ArrowData[];
    canSort: boolean;
    connectionPoints: Dictionary<ConnectionPoint>;
    digitalSets: Dictionary<DigitalSet>;
    displayed: IIdentifierVm[];
    dropCallback: Function;
    flatMode = FlatMode;
    form: angular.IFormController;
    groups: Group[];
    hasMore: boolean;
    identifiers: IIdentifierVm[];
    identifierType = IdentifierType;
    isEditMode: boolean;
    isLoading: boolean;
    isStoreLoading: boolean;
    isSelectionPrevented: boolean;
    loadingMessage: string;
    menu: IDisplayedColumn[];
    pageLength: number;
    pageNumber: number;
    selectedRowIndex: number;
    selectedGroup: Group;
    selectedIdentifier: Identifier;
    selectedItem: Identifier;
    sortConfig: ISortConfiguration;
    sortReverse = false;
    sortType = 'sortingOrder';
    tagGuidToSelect: string;
    variables: VariableType[] = [];
    viewModels: IValueViewModel[] = [];

    private _variableSubTypes: { [guid: string]: VariableSubType };
    private _qualifiers: { [guid: string]: Qualifier };
    private _garbarges: (() => void)[] = [];

    static $inject = [
        '$scope',
        '$rootScope',
        '$q',
        'storeService',
        'variableCascadingService',
        'identifierStateService',
        'translatedLoggerService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$q: angular.IQService,
        private _storeService: IStoreService,
        private _variableCascadingService: IVariableCascadingService,
        private _identifierStateService: IIdentifierStateService,
        private _loggerService: ITranslatedLoggerService
    ) {
        this.sortConfig = {
            animation: 150,
            disabled: true,
            handle: '.drag-handle',
            onSort: (evt) => this.sort(evt)
        };
    }

    $onInit() {
        this.identifiers = [];
        this.selectedIdentifier = null;
        this.pageLength = defaultItemPerPage;
        this.pageNumber = 1;

        this._garbarges.push(this._$rootScope.$on(groupSelected, (_event, data) => {
            const group = data;
            this.selectedGroup = group;
            this.selectedRowIndex = -1;
            if (group) {
                this.refresh(group.identifierCollection);
            }
        }));

        this._$scope.$on(toggleEditMode, this.toggleEditMode());

        this._$scope.$on(groupSaved, (_$event, newValue) =>
            this.tryReselectTag(newValue.tag.guid)
        );

        this._$scope.$on(identifierSaved, (_$event, _newValue) => {
            if (this.selectedItem) {
                this.selectedItem = this.identifiers.filter(i => i.guid === this.selectedItem.guid)[0];
            }
        });

        this._$scope.$on(tagSaved, ($event, _newValue) =>
            this.refreshView(this.selectedIdentifier, $event, false)
        );

        this._$scope.$watch(
            () => this.selectedGroup,
            (newValue, oldValue) => {
                if (newValue && (newValue.identifierCollection !== oldValue?.identifierCollection)) {
                    this.refresh(newValue.identifierCollection);
                }
            }
        );

        this._$scope.$watch(
            () => this.pageNumber,
            () => this.updateDisplay()
        );

        this._$scope.$watch(
            () => this.selectedItem,
            () => this.updateSelectedItem());

        this._$scope.$watch(
            () => this.isSelectionPrevented,
            () => this.sortConfig.disabled = !this._$rootScope.isEditMode || !this.canSort || this.isSelectionPrevented
        );

        this.menu = [
            { isVisible: true, property: 'name', type: 'name', label: 'Name' },
            { isVisible: true, property: 'tagName', type: 'tag', label: 'Tag/Identifier' },
            { isVisible: true, property: 'shortComment', type: 'shortComment', label: 'Short comment' },
            { isVisible: false, property: 'longComment', type: 'longComment', label: 'Long comment' },
            { isVisible: false, property: 'documentation', type: 'documentation', label: 'Documentation' },
            { isVisible: true, property: 'variableTypeName', type: 'type', label: 'Type' },
            { isVisible: true, property: 'variableSubTypeName', type: 'subtype', label: 'Subtype' },
            { isVisible: true, property: 'qualifierName', type: 'qualifier', label: 'Qualifier' },
            { isVisible: true, property: 'unitName', type: 'unit', label: 'Unit' },
            { isVisible: true, property: 'target', type: 'target', label: 'Target' },
            { isVisible: false, property: 'pfd', type: 'pfd', label: 'PFD' },
            { isVisible: false, property: 'pid', type: 'pid', label: 'PID' },
            { isVisible: false, property: 'dataSheet', type: 'dataSheet', label: 'Data sheet' },
            { isVisible: false, property: 'defaultValue', type: 'defaultValue', label: 'Default value' },
            { isVisible: false, property: 'min', type: 'min', label: 'Min' },
            { isVisible: false, property: 'max', type: 'max', label: 'Max' },
            { isVisible: false, property: 'stdDevThreshold', type: 'stdDevThreshold', label: 'Std dev threshold' },
            { isVisible: false, property: 'flatMode', type: 'flat', label: 'Flat' },
            { isVisible: false, property: 'resolution', type: 'resolution', label: 'Resolution' },
            { isVisible: false, property: 'resolutionPercentage', type: 'resolutionPercentage', label: 'Resolution%' },
            { isVisible: false, property: 'source', type: 'source', label: 'Source' },
            { isVisible: false, property: 'sortingOrder', type: 'sortingOrder', label: 'Sorting order' },
            { isVisible: false, property: 'customProperties', type: 'customProperties', label: 'Own fields' }
        ];

        this.isStoreLoading = true;
        this._garbarges.push(this._storeService.register(
            store => this.reloadStore(store),
            [
                StoreElement.Variables,
                StoreElement.ValueViewModels,
                StoreElement.ConnectionPoints,
                StoreElement.Identifiers,
                StoreElement.Groups,
                StoreElement.DigitalSets
            ]
        ));
    }

    $onDestroy() {
        this._garbarges.forEach(w => w());
    }

    private reloadStore(store: Store) {
        this.variables = _.values(store.variablesTypes.dictionary);
        this._variableSubTypes = store.variableSubTypes.dictionary;
        this._qualifiers = store.qualifiers.dictionary;
        this.connectionPoints = store.connectionPoints.dictionary;
        this.viewModels = _.values(store.valueViewModels.dictionary);
        this.groups = _.values(store.groups.dictionary);
        this.allIdentifiers = _.values(store.identifiers.dictionary);
        this.digitalSets = store.digitalSets.dictionary;
        this.isStoreLoading = false;

        // reselect group if needed to provoke refresh
        if (this.selectedGroup && !this.isStoreLoading) {
            const newSelected = store.groups.dictionary[this.selectedGroup.guid];
            // prevent double refresh if we change the selected
            if (newSelected.guid === this.selectedGroup.guid) {
                this.refresh(this.selectedGroup.identifierCollection);
            } else {
                this.selectedGroup = newSelected;
            }
        }
    }

    private deleteIdentifierViaStore(groupGuid: string, identifierGuid: string): angular.IPromise<void> {
        return this._storeService.dispatchers().identifiers.deleteOne(groupGuid, identifierGuid);
    }

    private updateSelectedItem() {
        if (this.selectedItem === null) {
            this.selectedRowIndex = -1;
        }
    }

    private refreshView(newValue: Identifier, $event, displayDirtyMessage: boolean) {
        if (newValue) {
            this.refresh(this.selectedGroup.identifierCollection);
            const index = _.findIndex(this.displayed, i => i.guid === newValue.guid);
            this.setClickedRow(newValue, index === undefined ? this.displayed.length - 1 : index, $event, displayDirtyMessage);
        } else {
            this.selectedItem = null;
            this.selectedRowIndex = -1;
        }
    }

    private tryReselectTag(tagGuid: string) {
        const index = _.findIndex(this.displayed, i => i.tagDefinitionGuid === tagGuid);
        if (index >= 0) {
            this.setClickedRow(this.displayed[index], index);
            this.tagGuidToSelect = null;
        } else {
            this.tagGuidToSelect = tagGuid;
        }
    }

    private sort(evt) {
        this.selectedItem = null;
        this.selectedRowIndex = -1;
        evt.models.forEach((s, i) => {
            if (s) {
                s.sortingOrder = i;
            }
        });

        // silently save using ordering api
        this._storeService.dispatchers().identifiers
            .redorderIdentifiers(this.identifiers, this.selectedGroup);
    }

    private refresh(identifiers: Identifier[]) {
        this.isLoading = true;

        try {
            let newIdentifiers: IIdentifierVm[] = [];
            if (this.viewModels.length && this.connectionPoints) {
                newIdentifiers = this._identifierStateService.mergeViewModels(
                    identifiers,
                    this.viewModels,
                    this.connectionPoints,
                    this.digitalSets
                );
            }
            this.identifiers = newIdentifiers;
            this.updateDisplay();
        } catch (e) {
            console.error('Cannot merge identifier viewmodel');
            console.error(e);

        }
        this.isLoading = false;
    }

    private updateDisplay() {
        if (this.identifiers) {
            const min = Math.min(this.pageNumber * this.pageLength, this.identifiers.length);
            this.displayed = this.identifiers.slice(0, min);
            if (this.tagGuidToSelect) {
                this.tryReselectTag(this.tagGuidToSelect);
            }

            // reselect the identifier
            try {
                if (this.selectedRowIndex && this.selectedRowIndex < this.displayed.length) {
                    const selectedItem = this.displayed[this.selectedRowIndex];
                    this.setClickedRow(selectedItem, this.selectedRowIndex);
                } else if (this.selectedItem) {
                    const selected = this.displayed.filter(i => i.guid === this.selectedItem.guid);
                    if (selected.length) {
                        const first = selected[0];
                        const index = this.displayed.indexOf(first);
                        this.setClickedRow(first, index);
                    }
                }
            } catch (e) {
                console.error(e);
            }
            this.hasMore = (min < this.identifiers.length);
        }
    }

    more(): void {
        this.pageNumber++;
    }

    isVisible(column: string): boolean {
        const found = _.find(this.menu, m => m.type === column);
        return found ? found.isVisible : false;
    }

    setDirty(identifier) {
        identifier.isDirty = true;
    }

    save() {
        this.loadingMessage = 'Saving identifiers';
        this.isLoading = true;
        const defered = this._$q.defer<void>();

        this._storeService.dispatchers().identifiers
            .updateMany(this.identifiers, this.selectedGroup)
            .then(data => {
                this.selectedGroup.identifierCollection = data;
                this.form.$setPristine();
                // store will reload and refresh for us already
                defered.resolve();
            })
            .catch(() => defered.reject('Failed to save identifiers'))
            .finally(() => this.isLoading = false);

        return defered.promise;
    }

    showModal(selector: string, identifier: Identifier, $event: angular.IAngularEvent): void {
        if (!this.isSelectionPrevented && !$event.defaultPrevented) {
            if (identifier) {
                this.selectedIdentifier = identifier;
            }
            $(selector).modal('toggle');
        }
    }

    checkNames(newName, identifier) {
        if (_.some(this.identifiers, i => i.name === newName && i.guid !== identifier.guid)) {
            return 'Duplicate identifier name';
        }
    }

    checkShortComment(shortComment: string) {
        if (shortComment.length > 16) {
            return 'Short comment is maximum 16 characters';
        }
    }

    setClickedRow(item: Identifier, index: number, $event?: angular.IAngularEvent, displayDirtyMessage: boolean = true): void {
        if (!this.isSelectionPrevented) {
            this.selectedRowIndex = index;
            this.selectedItem = item;
            this._$rootScope.$broadcast(identifierSelected, item);
            if ($event) {
                $event.preventDefault();
            }
        } else {
            if (this.selectedItem && displayDirtyMessage) {
                this._identifierStateService.issueSelectionWarning();
            }
            if ($event && $event.stopPropagation) {
                $event.stopPropagation();
            }
        }
    }

    // bound to toggleEditMode from WidgetBase
    toggleEditMode() {
        const self = this;
        return (_event, _argument) => {
            if ((this._$rootScope.isEditMode === undefined || this._$rootScope.isEditMode === false)
                && (self._identifierStateService && self._identifierStateService.formController)) {
                self._identifierStateService.formController.$setPristine();
            }
            this.sortConfig.disabled = !this._$rootScope.isEditMode || !this.canSort || this.isSelectionPrevented;
        };
    }

    dropOnRow(identifier: IValueViewModel, dragObject: { guid: string; objectType: ObjectType }) {
        const checks = [
            isItself,
            isCorrectObjectType,
            isCorrectIdentifierType,
            isTagOrValidated
        ];
        let message: string;
        for (const check of checks) {
            message = check(identifier, dragObject);
            if (message) {
                this._loggerService.logError(message);
                break;
            }
        }
        if (!message) {
            if (this.dropCallback) {
                this.dropCallback({ identifier: identifier, dragObject: dragObject });
            }
        }
    }

    deleteIdentifier() {
        const guid = this.selectedItem.guid;
        this.deleteIdentifierViaStore(this.selectedGroup.guid, this.selectedItem.guid)
            .then(() => {
                _.remove(this.selectedGroup.identifierCollection, i => i.guid === guid);
                this.selectedIdentifier = null;
                this.selectedRowIndex = -1;
                this.selectedItem = null;
                this._$rootScope.$broadcast(identifierDeleted, guid, this.selectedGroup);
            });
    }

    changeSubTypes(identifier, variableTypeGuid: string) {
        identifier.selectedVariableTypeGuid = variableTypeGuid;
        identifier.variableTypeGuid = variableTypeGuid;
        const vt = _.find(this.variables, v => v.guid === variableTypeGuid);
        if (vt) {
            const subtypes = vt.variableSubTypeGuidCollection.map(d => this._variableSubTypes[d]);
            const defaultSubType = getDefaultSubtype(identifier.variableSubTypeGuid, subtypes.map(d => { return { key: d.guid, value: d.name }; }));
            if (defaultSubType && defaultSubType !== identifier.variableSubTypeGuid) {
                identifier.variableSubTypeGuid = defaultSubType;
            }
            const qualifiers = vt.qualifierCollection.map(d => this._qualifiers[d]);
            const defaultQualifier = getDefaultQualifier(identifier.qualifierGuid, qualifiers.map(d => { return { key: d.guid, value: d.name }; }));
            if (defaultQualifier && defaultQualifier !== identifier.qualifierGuid) {
                identifier.qualifierGuid = defaultQualifier;
            }
            identifier.unitGuid = vt.internalUnitGuid;
        }
        this.setDirty(identifier);
    }

    variableSubTypes(variableTypeGuid) {
        return this._variableCascadingService.variableSubTypes(variableTypeGuid);
    }

    variableUnits(variableTypeGuid: string) {
        return this._variableCascadingService.variableUnits(variableTypeGuid);
    }

    qualifiers(variableTypeGuid: string) {
        return this._variableCascadingService.variableQualifiers(variableTypeGuid);
    }

    getVariableSubTypeName(variableTypeGuid, variableSubtypeGuid) {
        return this._variableCascadingService.getVariableSubTypeName(variableTypeGuid, variableSubtypeGuid);
    }
}

export const wtIdentifiers: ComponentDefinition = {
    componentId: 'wtIdentifiers',
    controller: IdentifiersController,
    template: require('./identifiers.component.html'),
    controllerAs: 'vm',
    bindings: {
        canDelete: '@?',
        canDropOnRow: '@?',
        canEdit: '@?',
        canSort: '@?',
        displayed: '=?',
        dropCallback: '&?',
        form: '=?',
        isEditMode: '=?',
        isLoading: '=?',
        isSelectionPrevented: '=?',
        menu: '=?',
        pageNumber: '=?',
        selectedGroup: '=?',
        selectedItem: '=?',
    }
};